/**
 * A router, in about forty lines.
 *
 * Deliberately not a dependency. The console has a handful of screens, and
 * what it needs from a router is exactly three things: real URLs, working
 * back and forward, and deep links that survive a reload.
 *
 * Real URLs are not a nicety here. A streamer's bookmark and an OBS browser
 * source are addresses you cannot edit once they are out, so a console built
 * on in-memory state would be one that cannot be linked into (L59).
 */

export interface Route {
  /** The path segments after the leading slash, already decoded. */
  segments: string[];
  search: URLSearchParams;
  path: string;
}

/**
 * The console lives under `/studio`, not at the root.
 *
 * `tomo.channel/{slug}` is a streamer's PUBLIC home, and `/aurora/rewards` is
 * far more likely to become a public reward showcase than an admin screen.
 * Interleaving the two makes every path ambiguous about who it is for
 * (docs/architecture/urls.md). The root is the viewer-facing landing.
 *
 * Stripped here and added back in `hrefFor`, so every screen keeps reading
 * `segments[0]` as the channel id and nothing else in the console knows this
 * moved.
 */
export const STUDIO_BASE = '/studio';

/** Is this address inside the console at all? */
export function isStudio(pathname: string): boolean {
  return pathname === STUDIO_BASE || pathname.startsWith(`${STUDIO_BASE}/`);
}

export function parseRoute(url: string): Route {
  // Collapse the leading slashes FIRST. `new URL('//a/b', base)` is
  // protocol-relative: it reads `a` as the HOST and the route silently loses
  // its first segment — which on this console is the channel id.
  const asPath = `/${url.replace(/^\/+/, '')}`;
  const parsed = new URL(asPath, 'https://console.invalid');
  const all = parsed.pathname.split('/').filter((s) => s.length > 0).map(decodeURIComponent);
  return {
    // The base is not a route segment. Dropping it here means every screen
    // still reads segments[0] as the channel id.
    segments: all[0] === 'studio' ? all.slice(1) : all,
    search: parsed.searchParams,
    path: parsed.pathname,
  };
}

export function hrefFor(segments: readonly string[]): string {
  const path = segments.map(encodeURIComponent).join('/');
  return path.length > 0 ? `${STUDIO_BASE}/${path}` : STUDIO_BASE;
}

type Listener = (route: Route) => void;
const listeners = new Set<Listener>();

export function currentRoute(): Route {
  return parseRoute(window.location.pathname + window.location.search);
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  const route = currentRoute();
  for (const listener of listeners) listener(route);
}

export function onRouteChange(listener: Listener): () => void {
  listeners.add(listener);
  // popstate is the back and forward buttons. Without it the URL changes and
  // the screen does not, which is the single most common way a hand-rolled
  // router is broken.
  const onPop = () => { listener(currentRoute()); };
  window.addEventListener('popstate', onPop);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', onPop);
  };
}

/**
 * Intercept in-app link clicks so an <a href> is a real link AND a client-side
 * navigation.
 *
 * Modified clicks are left alone on purpose: cmd-click to open in a new tab is
 * something people do constantly in an admin console, and swallowing it is the
 * kind of small breakage that makes a UI feel wrong without anyone naming why.
 */
export function shouldIntercept(
  event: MouseEvent,
  anchor: HTMLAnchorElement,
  /** Passed in rather than read off `window`, so this stays a pure function. */
  origin: string
): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;
  // An absolute URL to somewhere else is a real link, not a route.
  return anchor.origin === origin;
}

/**
 * Paths at the apex that are not a community, whatever somebody named it.
 *
 * The server refuses these as slugs too (`packages/auth` reserves them), so
 * this is the browser agreeing rather than deciding. Kept because the console
 * answers the apex for every path the rooms app does not, and asking the API
 * whether `/favicon.svg` is a community would be a request per stray file.
 */
const NOT_A_COMMUNITY = new Set([
  'studio', 'live', 'c', 'v1', 'healthz', 'collection', 'trades', 'reports',
  'privacy', 'terms', 'about', 'favicon.svg', 'robots.txt', 'assets',
]);

/**
 * The community slug this address names, or null.
 *
 * ONE SEGMENT ONLY, plus `/{slug}/admin`. A community owns its name at the
 * apex (ADR 0047) and nothing below it except the admin screen, so a deeper
 * path is not a community page — treating it as one would swallow every
 * address this product has not invented yet.
 *
 * Anything with a dot is refused: those are files, and `/favicon.svg` asking
 * the API whether it is a community is a request nobody wanted.
 */
export function communityRoute(pathname: string): { slug: string; admin: boolean } | null {
  const parts = pathname.split('/').filter((p) => p.length > 0).map(decodeURIComponent);
  const [slug, next, ...rest] = parts;
  if (slug === undefined || rest.length > 0) return null;
  if (next !== undefined && next !== 'admin') return null;
  if (slug.includes('.') || NOT_A_COMMUNITY.has(slug.toLowerCase())) return null;
  return { slug: slug.toLowerCase(), admin: next === 'admin' };
}
